import type { ReactNode } from "react";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { getAppBarAdStatusByContent, getAppBarVariant } from "~/components/app-bar/helpers";
import { BaseEntityLink } from "~/components/base-entity-link";
import type { LinkEntity } from "~/components/base-entity-link/types";
import type { Props as BaseLinkProps } from "~/components/common/base-link";
import type { entityLink$key } from "~/queries/__generated__/entityLink.graphql";

export type Props = {
  children?: ReactNode;
  reference: entityLink$key;
} & Omit<BaseLinkProps, "href" | "as" | "pathname">;

export const EntityLink = forwardRef<HTMLAnchorElement, Props>((props, reference) => {
  const { children, query, reference: reference_, ...remainingProps } = props;
  const linkableEntity = useFragment(
    graphql`
      fragment entityLink on Base {
        __typename
        entityId
        entityUuid
        urlAlias
        urlRedirect {
          toUrl
        }
        application {
          entityId
        }
        ...helpersAppBarVariantArticle
        ...helpersAppBarAdStatusBase
      }
    `,
    reference_,
  );

  const { __typename, application, entityId, entityUuid, urlAlias, urlRedirect } = linkableEntity;

  const linkEntity: LinkEntity = {
    applicationId: application?.entityId,
    entityId,
    routeType: __typename,
    urlAlias,
    urlRedirectPath: urlRedirect?.toUrl,
  };

  const appBarVariant = getAppBarVariant(linkableEntity);
  const appBarAdStatus = getAppBarAdStatusByContent(linkableEntity);

  return (
    <BaseEntityLink
      linkEntity={linkEntity}
      {...remainingProps}
      query={{
        ...query,
        appBarAdStatus,
        appBarVariant,
        entityUuid,
      }}
      ref={reference}
    >
      {children}
    </BaseEntityLink>
  );
});

EntityLink.displayName = "EntityLink";
